import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Select,
  Chip,
  Fade,
  TextField
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import EditIcon from '@mui/icons-material/Edit';
import EmailIcon from '@mui/icons-material/Email';
import AssignmentIcon from '@mui/icons-material/Assignment';
import * as XLSX from 'xlsx';
import './EmployeeBulkActions.css';

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const EmployeeBulkActions = ({
  selectedEmployees,
  onClearSelection,
  onBulkUpdateDesignation,
  onBulkUpdateServiceDay,
  onBulkAssignRoute,
  onSendNotification,
  onExportComplete
}) => {
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const [designationDialogOpen, setDesignationDialogOpen] = useState(false);
  const [serviceDayDialogOpen, setServiceDayDialogOpen] = useState(false);
  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);
  const [selectedDesignation, setSelectedDesignation] = useState('');
  const [selectedServiceDay, setSelectedServiceDay] = useState('');
  const [notificationMessage, setNotificationMessage] = useState('');

  const handleExportClick = (event) => {
    setExportAnchorEl(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportAnchorEl(null);
  };

  // Export to CSV
  const handleExportCSV = () => {
    const csvData = selectedEmployees.map(employee => ({
      'Employee Name': employee.empObject?.empName || '',
      'Designation': employee.empObject?.empDesignation || '',
      'Contact': employee.empObject?.empContact || '',
      'Email': employee.empObject?.empEmail || '',
      'Service Day': employee.empObject?.serviceDay || '',
      'Address': employee.empObject?.empAddress || '',
      'City': employee.empObject?.empCity || '',
      'State': employee.empObject?.empState || '',
      'ZIP': employee.empObject?.empZip || ''
    }));

    const headers = Object.keys(csvData[0]);
    const csvRows = [
      headers.join(','),
      ...csvData.map(row =>
        headers.map(header => {
          const value = row[header] || '';
          return value.toString().includes(',') ? `"${value.replace(/"/g, '""')}"` : value;
        }).join(',')
      )
    ];

    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `employees_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    handleExportClose();
    if (onExportComplete) onExportComplete('CSV');
  };

  // Export to Excel
  const handleExportExcel = () => {
    const excelData = selectedEmployees.map(employee => ({
      'Employee Name': employee.empObject?.empName || '',
      'Designation': employee.empObject?.empDesignation || '',
      'Contact': employee.empObject?.empContact || '',
      'Email': employee.empObject?.empEmail || '',
      'Service Day': employee.empObject?.serviceDay || '',
      'Address': employee.empObject?.empAddress || '',
      'City': employee.empObject?.empCity || '',
      'State': employee.empObject?.empState || '',
      'ZIP': employee.empObject?.empZip || ''
    }));

    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Employees');

    const maxWidth = 50;
    const colWidths = Object.keys(excelData[0] || {}).map(key => ({
      wch: Math.min(
        Math.max(
          key.length,
          ...excelData.map(row => (row[key] || '').toString().length)
        ),
        maxWidth
      )
    }));
    ws['!cols'] = colWidths;

    XLSX.writeFile(wb, `employees_export_${new Date().toISOString().split('T')[0]}.xlsx`);

    handleExportClose();
    if (onExportComplete) onExportComplete('Excel');
  };

  // Bulk update designation
  const handleDesignationUpdate = () => {
    if (selectedDesignation && onBulkUpdateDesignation) {
      onBulkUpdateDesignation(selectedEmployees, selectedDesignation);
      setDesignationDialogOpen(false);
      setSelectedDesignation('');
    }
  };

  // Bulk update service day
  const handleServiceDayUpdate = () => {
    if (selectedServiceDay && onBulkUpdateServiceDay) {
      onBulkUpdateServiceDay(selectedEmployees, selectedServiceDay);
      setServiceDayDialogOpen(false);
      setSelectedServiceDay('');
    }
  };

  // Send notification
  const handleSendNotification = () => {
    if (notificationMessage.trim() && onSendNotification) {
      onSendNotification(selectedEmployees, notificationMessage);
      setNotificationDialogOpen(false);
      setNotificationMessage('');
    }
  };

  if (selectedEmployees.length === 0) {
    return null;
  }

  return (
    <>
      <Fade in={true}>
        <Paper
          elevation={3}
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1000,
            mb: 2,
            p: { xs: 1.5, sm: 2 },
            backgroundColor: '#E3F2FD',
            borderLeft: '4px solid #2196F3',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            gap: { xs: 1.5, sm: 2 }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap' }}>
            <Chip
              label={`${selectedEmployees.length} selected`}
              color="primary"
              size={window.innerWidth < 600 ? 'small' : 'medium'}
              sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.9rem' } }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', sm: 'block' } }}>
              Bulk actions available
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, flexWrap: 'wrap', justifyContent: { xs: 'space-between', sm: 'flex-end' } }}>
            {/* Export Button */}
            <Tooltip title="Export selected employees">
              <Button
                variant="contained"
                startIcon={<FileDownloadIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />}
                onClick={handleExportClick}
                size={window.innerWidth < 600 ? 'small' : 'medium'}
                sx={{
                  backgroundColor: '#10B981',
                  '&:hover': { backgroundColor: '#059669' },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  padding: { xs: '4px 8px', sm: '6px 16px' },
                  minWidth: { xs: 'auto', sm: '64px' },
                  flex: { xs: '1', sm: '0 1 auto' }
                }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Export</Box>
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Export</Box>
              </Button>
            </Tooltip>

            {/* Update Designation */}
            <Tooltip title="Update designation for selected employees">
              <Button
                variant="contained"
                startIcon={<AssignmentIcon sx={{ fontSize: { xs: 16, sm: 20 }, display: { xs: 'none', sm: 'inline-flex' } }} />}
                onClick={() => setDesignationDialogOpen(true)}
                size={window.innerWidth < 600 ? 'small' : 'medium'}
                sx={{
                  backgroundColor: '#8B5CF6',
                  '&:hover': { backgroundColor: '#7C3AED' },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  padding: { xs: '4px 8px', sm: '6px 16px' },
                  minWidth: { xs: 'auto', sm: '64px' },
                  flex: { xs: '1', sm: '0 1 auto' }
                }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Update Role</Box>
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Role</Box>
              </Button>
            </Tooltip>

            {/* Update Service Day */}
            <Tooltip title="Update service day for selected employees">
              <Button
                variant="contained"
                startIcon={<EditIcon sx={{ fontSize: { xs: 16, sm: 20 }, display: { xs: 'none', sm: 'inline-flex' } }} />}
                onClick={() => setServiceDayDialogOpen(true)}
                size={window.innerWidth < 600 ? 'small' : 'medium'}
                sx={{
                  backgroundColor: '#2196F3',
                  '&:hover': { backgroundColor: '#1976D2' },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  padding: { xs: '4px 8px', sm: '6px 16px' },
                  minWidth: { xs: 'auto', sm: '64px' },
                  flex: { xs: '1', sm: '0 1 auto' }
                }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Update Day</Box>
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Day</Box>
              </Button>
            </Tooltip>

            {/* Send Notification */}
            <Tooltip title="Send notification to selected employees">
              <Button
                variant="contained"
                startIcon={<EmailIcon sx={{ fontSize: { xs: 16, sm: 20 }, display: { xs: 'none', sm: 'inline-flex' } }} />}
                onClick={() => setNotificationDialogOpen(true)}
                size={window.innerWidth < 600 ? 'small' : 'medium'}
                sx={{
                  backgroundColor: '#F59E0B',
                  '&:hover': { backgroundColor: '#D97706' },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  padding: { xs: '4px 8px', sm: '6px 16px' },
                  minWidth: { xs: 'auto', sm: '64px' },
                  flex: { xs: '1', sm: '0 1 auto' }
                }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Notify</Box>
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Notify</Box>
              </Button>
            </Tooltip>

            {/* Clear Selection */}
            <Tooltip title="Clear selection">
              <IconButton
                onClick={onClearSelection}
                size={window.innerWidth < 600 ? 'small' : 'medium'}
                sx={{
                  backgroundColor: 'white',
                  '&:hover': { backgroundColor: '#f5f5f5' },
                  padding: { xs: '6px', sm: '8px' }
                }}
              >
                <CloseIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Paper>
      </Fade>

      {/* Export Menu */}
      <Menu
        anchorEl={exportAnchorEl}
        open={Boolean(exportAnchorEl)}
        onClose={handleExportClose}
      >
        <MenuItem onClick={handleExportCSV}>
          <FileDownloadIcon sx={{ mr: 1 }} />
          Export as CSV
        </MenuItem>
        <MenuItem onClick={handleExportExcel}>
          <FileDownloadIcon sx={{ mr: 1 }} />
          Export as Excel
        </MenuItem>
      </Menu>

      {/* Designation Update Dialog */}
      <Dialog
        open={designationDialogOpen}
        onClose={() => setDesignationDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        fullScreen={window.innerWidth < 600}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>Update Designation</DialogTitle>
        <DialogContent sx={{ minWidth: { xs: 'auto', sm: 400 }, pt: 2 }}>
          <Typography variant="body2" sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
            Update designation for {selectedEmployees.length} selected employee(s)
          </Typography>
          <TextField
            fullWidth
            label="Designation"
            value={selectedDesignation}
            onChange={(e) => setSelectedDesignation(e.target.value)}
            placeholder="e.g., Technician, Manager, Supervisor"
            size={window.innerWidth < 600 ? 'small' : 'medium'}
          />
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 1 }, gap: 1 }}>
          <Button
            onClick={() => setDesignationDialogOpen(false)}
            size={window.innerWidth < 600 ? 'small' : 'medium'}
            fullWidth={window.innerWidth < 600}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDesignationUpdate}
            variant="contained"
            disabled={!selectedDesignation.trim()}
            size={window.innerWidth < 600 ? 'small' : 'medium'}
            fullWidth={window.innerWidth < 600}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Service Day Update Dialog */}
      <Dialog
        open={serviceDayDialogOpen}
        onClose={() => setServiceDayDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        fullScreen={window.innerWidth < 600}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>Update Service Day</DialogTitle>
        <DialogContent sx={{ minWidth: { xs: 'auto', sm: 400 }, pt: 2 }}>
          <Typography variant="body2" sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
            Update service day for {selectedEmployees.length} selected employee(s)
          </Typography>
          <FormControl fullWidth>
            <Select
              value={selectedServiceDay}
              onChange={(e) => setSelectedServiceDay(e.target.value)}
              displayEmpty
              size={window.innerWidth < 600 ? 'small' : 'medium'}
            >
              <MenuItem value="" disabled>
                Select Service Day
              </MenuItem>
              {DAYS_OF_WEEK.map(day => (
                <MenuItem key={day} value={day}>
                  {day}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 1 }, gap: 1 }}>
          <Button
            onClick={() => setServiceDayDialogOpen(false)}
            size={window.innerWidth < 600 ? 'small' : 'medium'}
            fullWidth={window.innerWidth < 600}
          >
            Cancel
          </Button>
          <Button
            onClick={handleServiceDayUpdate}
            variant="contained"
            disabled={!selectedServiceDay}
            size={window.innerWidth < 600 ? 'small' : 'medium'}
            fullWidth={window.innerWidth < 600}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Dialog */}
      <Dialog
        open={notificationDialogOpen}
        onClose={() => setNotificationDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        fullScreen={window.innerWidth < 600}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>Send Notification</DialogTitle>
        <DialogContent sx={{ minWidth: { xs: 'auto', sm: 400 }, pt: 2 }}>
          <Typography variant="body2" sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
            Send notification to {selectedEmployees.length} selected employee(s)
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Message"
            value={notificationMessage}
            onChange={(e) => setNotificationMessage(e.target.value)}
            placeholder="Enter your message here..."
            size={window.innerWidth < 600 ? 'small' : 'medium'}
          />
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 1 }, gap: 1 }}>
          <Button
            onClick={() => setNotificationDialogOpen(false)}
            size={window.innerWidth < 600 ? 'small' : 'medium'}
            fullWidth={window.innerWidth < 600}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSendNotification}
            variant="contained"
            disabled={!notificationMessage.trim()}
            size={window.innerWidth < 600 ? 'small' : 'medium'}
            fullWidth={window.innerWidth < 600}
          >
            Send
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EmployeeBulkActions;
